/**
 * デバイス詳細 `/mdm/devices/:deviceId`(UX1-b2 で Drawer から独立したページに)。
 * 詳細テンプレート(`ui/DetailPage`): ヘッダー + サマリー帯 + タブ(概要 / アプリ / MDM / Android / ポリシー)。
 * MdmTab / AndroidTab は Drawer のときの中身をそのまま載せ替えただけ。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Flex, Popconfirm, Select, Spin, Table, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import type { Device, DeviceApp, Equipment } from "../../lib/api";
import { hasFeature } from "../../lib/features";
import { jst } from "../../lib/format";
import { hasRole } from "../../lib/roles";
import { useApi, useMe } from "../../lib/useApi";
import { useDebounced } from "../../lib/useDebounced";
import { DetailPage, KeyValueList, Section, StatusTag, TimeAgo } from "../../ui";
import { DeviceAgentTokens } from "./AgentDistCards";
import { AndroidTab } from "./AndroidTab";
import { MdmTab } from "./MdmTab";
import { DeviceRegistryPolicies } from "./RegistryTargets";
import { policyStateTone, sourceLabel } from "./constants";
import { osText, platformTag, policyProgress, sourceTag, userCell } from "./deviceBits";
import { errorText, useDevice, useInvalidateMdm, useMdmPerms } from "./hooks";

/** 利用者の付け替え。従業員は全アプリ共通の名簿(/api/core/directory)から引く。 */
function Reassign({ device, onDone }: { device: Device; onDone: () => void }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<number | undefined>();

  const people = useQuery({
    queryKey: ["mdm", "directory", q],
    queryFn: () =>
      api.directoryEmployees(
        new URLSearchParams({ limit: "50", status: "active", ...(q ? { q } : {}) }),
      ),
  });

  const save = useMutation({
    mutationFn: (body: { employee_id?: number | null; unassign?: boolean }) =>
      api.updateDevice(device.id, body),
    onSuccess: () => {
      void message.success("利用者を更新した");
      invalidate();
      onDone();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Flex gap={8} wrap align="center">
      <Select
        className="ui-pick-wide"
        placeholder="利用者(在籍中の人だけ)"
        showSearch
        filterOption={false}
        value={pick}
        loading={people.isFetching}
        onSearch={setQ}
        onChange={(v: number) => setPick(v)}
        options={(people.data?.items ?? []).map((e) => ({
          value: e.id,
          label: `${e.name}(${e.emp_code} / ${e.email})`,
        }))}
      />
      <Button
        type="primary"
        loading={save.isPending}
        onClick={() => {
          if (!pick) {
            void message.error("利用者を選ぶ");
            return;
          }
          save.mutate({ employee_id: pick });
        }}
      >
        付け替える
      </Button>
      <Button
        danger
        disabled={device.employee_id == null && !device.employee_email}
        loading={save.isPending}
        onClick={() => save.mutate({ unassign: true })}
      >
        利用者を外す
      </Button>
    </Flex>
  );
}

/** 備品の 1 行の表示(管理番号 機種名(製造番号 / 貸与先))。 */
function equipmentLabel(e: Equipment): string {
  const name = [e.maker, e.name].filter(Boolean).join(" ");
  return `${e.asset_no} ${name}(${e.serial || "製造番号なし"} / ${
    e.holder?.employee_name || "貸与なし"
  })`;
}

/**
 * 台帳(人事アプリの備品マスタ)のどの個体がこの端末か。
 *
 * 備品を書くのは hr だけなので、紐づけも hr の API(PUT /api/hr/equipment/{id}/device)を叩く。
 * 製造番号一致の自動紐づけで相関しない個体(表記ゆれ・未入力)を、ここで手で選ぶ。
 * 端末の利用者と備品の貸与先が食い違っても警告を出すだけで書き換えない。
 */
function DeviceEquipment({ device }: { device: Device }) {
  const api = useApi();
  const me = useMe();
  const qc = useQueryClient();
  const canView = hasRole(me.data, "hr:viewer");
  const canWrite = hasRole(me.data, "hr:operator");
  const [search, setSearch] = useState("");
  const [pick, setPick] = useState<number | undefined>();
  const term = useDebounced(search);

  const linked = useQuery({
    queryKey: ["hr", "equipment", "by-device", device.id],
    queryFn: () =>
      api.equipment(new URLSearchParams({ device_id: String(device.id), limit: "20" })),
    enabled: canView,
  });
  const candidates = useQuery({
    queryKey: ["hr", "equipment", "pick-for-device", term],
    queryFn: async () => {
      const res = await api.equipment(
        new URLSearchParams({ limit: "50", ...(term ? { q: term } : {}) }),
      );
      // 廃棄・返納した個体は選ばせない(status は 1 つしか指定できないので手元で除く)。
      // total は除く前の件数。先頭 50 件で切れていれば案内して絞り込ませる
      return { items: res.items.filter((e) => e.status !== "retired"), total: res.total,
               fetched: res.items.length };
    },
    enabled: canWrite,
  });
  const save = useMutation({
    mutationFn: ({ id, deviceId }: { id: number; deviceId: number | null }) =>
      api.setEquipmentDevice(id, deviceId),
    onSuccess: (_, v) => {
      void message.success(v.deviceId == null ? "備品の紐づけを外した" : "台帳の備品を紐づけた");
      setPick(undefined);
      void qc.invalidateQueries({ queryKey: ["hr"] });
      void qc.invalidateQueries({ queryKey: ["mdm", "device", device.id] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (!canView)
    return <Typography.Text type="secondary">台帳は人事アプリの権限が要る</Typography.Text>;
  if (linked.isError) return <Alert type="error" showIcon message={errorText(linked.error)} />;
  const items = linked.data?.items ?? [];

  return (
    <Flex vertical gap={8}>
      {linked.isLoading ? (
        <Spin size="small" />
      ) : items.length === 0 ? (
        <Typography.Text>台帳に紐づいていない</Typography.Text>
      ) : (
        items.map((e) => (
          <Flex key={e.id} gap={8} wrap align="center">
            <Link to={`/hr/equipment?q=${encodeURIComponent(e.asset_no)}&id=${e.id}`}>
              {e.asset_no}
            </Link>
            <span>{[e.maker, e.name].filter(Boolean).join(" ") || "—"}</span>
            <Typography.Text type="secondary">{e.category}</Typography.Text>
            <span>貸与先: {e.holder?.employee_name || "貸与なし"}</span>
            {e.device_link_mode === "manual" && (
              <Typography.Text type="secondary">(手動)</Typography.Text>
            )}
            {device.employee_id != null &&
              e.holder != null &&
              device.employee_id !== e.holder.employee_id && (
                <Typography.Text type="warning">
                  端末の利用者({device.employee_name || device.employee_email})と貸与先が違う
                </Typography.Text>
              )}
            {canWrite && (
              <Popconfirm
                title="備品の紐づけを外す"
                description="外すと自動(製造番号一致)に戻る"
                okText="外す"
                cancelText="やめる"
                onConfirm={() => save.mutate({ id: e.id, deviceId: null })}
              >
                <Button size="small" danger loading={save.isPending}>
                  外す
                </Button>
              </Popconfirm>
            )}
          </Flex>
        ))
      )}
      {canWrite && (
        <Flex gap={8} wrap align="center">
          <Select
            className="ui-pick-wide"
            placeholder="管理番号・機種名・製造番号で検索"
            showSearch
            filterOption={false}
            value={pick}
            loading={candidates.isFetching}
            onSearch={setSearch}
            onChange={(v: number) => setPick(v)}
            options={(candidates.data?.items ?? []).map((e) => ({
              value: e.id,
              label: equipmentLabel(e),
            }))}
          />
          <Button
            type="primary"
            loading={save.isPending}
            onClick={() => {
              if (!pick) {
                void message.error("備品を選ぶ");
                return;
              }
              save.mutate({ id: pick, deviceId: device.id });
            }}
          >
            台帳から紐づける
          </Button>
          {candidates.isError && (
            <Alert type="error" showIcon message={errorText(candidates.error)} />
          )}
          {candidates.data && candidates.data.total > candidates.data.fetched && (
            <Typography.Text type="warning">
              {candidates.data.total} 件のうち先頭 {candidates.data.fetched} 件だけ見ている。
              管理番号・機種名で絞り込む
            </Typography.Text>
          )}
        </Flex>
      )}
    </Flex>
  );
}

export function DeviceDetailPage() {
  const { deviceId } = useParams();
  const id = Number(deviceId);
  const { canAdmin, canWrite, hasAndroid, hasApple, hasWindows } = useMdmPerms();
  const me = useMe();
  // 人事アプリの貸与品(備品マスタ)を契約していなければ「台帳の備品」は出さない
  const hasLoans = hasFeature(me.data, "hr.loans");
  const query = useDevice(Number.isFinite(id) ? id : null);

  if (query.isLoading)
    return (
      <Flex justify="center" style={{ padding: 64 }}>
        <Spin />
      </Flex>
    );
  if (query.isError || !query.data)
    return (
      <Alert
        type="error"
        showIcon
        message="デバイスを取得できなかった"
        description={query.error ? errorText(query.error) : "見つからない"}
      />
    );

  const d = query.data;
  const apps = (d.inventory?.apps ?? []) as DeviceApp[];
  const policies = Object.entries(d.policy_state ?? {});
  const isAndroid = d.source === "android";

  const appColumns: ColumnsType<DeviceApp> = [
    { title: "アプリ", width: 200, render: (_: unknown, row) => row.name || "—" },
    { title: "版", width: 200, render: (_: unknown, row) => row.version || "—" },
  ];
  const policyColumns: ColumnsType<[string, string]> = [
    { title: "ポリシー", width: 240, render: (_: unknown, [k]) => k },
    {
      title: "適用結果",
      width: 160,
      render: (_: unknown, [, v]) => (
        <StatusTag tone={policyStateTone(v)} dot>
          {v}
        </StatusTag>
      ),
    },
  ];

  return (
    <DetailPage
      back={{ to: "/mdm/devices", label: "デバイス台帳へ戻る" }}
      title={d.hostname}
      tags={
        <>
          {sourceTag(d.source)}
          {platformTag(d.os)}
        </>
      }
      sub={
        <>
          {d.serial && <span className="ui-num">{d.serial}</span>}
          {d.model && <span>{d.model}</span>}
        </>
      }
      summary={[
        { label: "OS", value: osText(d) },
        {
          label: "利用者",
          value: d.employee_name || d.employee_email,
          href: d.employee_id ? `/hr/employees/${d.employee_id}` : undefined,
          empty: "未割当",
        },
        { label: "ポリシー適用", value: policyProgress(d), empty: "なし" },
        { label: "最終チェックイン", value: d.last_seen ? <TimeAgo iso={d.last_seen} /> : undefined, empty: "まだ無い" },
        { label: "取得元", value: sourceLabel(d.source) },
      ]}
      tabs={[
        {
          key: "overview",
          label: "概要",
          children: (
            <>
              <Section title="基本情報">
                <KeyValueList
                  columns={2}
                  items={[
                    { label: "ホスト名", value: d.hostname, copyable: true },
                    { label: "シリアル", value: d.serial, copyable: true },
                    { label: "OS", value: osText(d) },
                    { label: "機種", value: d.model },
                    { label: "利用者", value: userCell(d) },
                    { label: "取得元", value: sourceTag(d.source), always: true },
                    { label: "最終チェックイン", value: d.last_seen ? jst(d.last_seen) : "" },
                    { label: "登録", value: d.created_at ? jst(d.created_at) : "" },
                    { label: "アプリ数", value: <span className="ui-num">{apps.length}</span>, always: true },
                    { label: "ポリシー適用", value: policyProgress(d) },
                  ]}
                />
              </Section>
              {hasLoans && (
                <Section title="台帳の備品">
                  <DeviceEquipment device={d} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    製造番号が一致する備品はチェックインで自動で紐づく。違う・未入力の備品はここで
                    台帳から選ぶ(手動は製造番号が違っても外れない。外すと自動に戻る)
                  </Typography.Text>
                  <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                    紐づけたとき利用者が片側だけなら、もう片側へ転記する(端末の利用者 ⇄ 備品の貸与先)
                  </Typography.Paragraph>
                </Section>
              )}
              {hasWindows && d.source === "agent" && (
                <Section title="この端末に配るレジストリ" flush>
                  <DeviceRegistryPolicies deviceId={d.id} />
                </Section>
              )}
              {hasWindows && d.source === "agent" && (
                <Section title="エージェントの端末トークン" flush>
                  <DeviceAgentTokens deviceId={d.id} canAdmin={canAdmin} />
                </Section>
              )}
              {canWrite && (
                <Section title="利用者を変える">
                  <Reassign device={d} onDone={() => void query.refetch()} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    ここで選んだ利用者は「確定」になり、レジストリ配布のグループ指定に使われる。
                    チェックインで名乗ったメールから自動で引いた利用者は自己申告のままで、グループ指定には使わない。
                    「利用者を外す」は名前もメールも消す(備品の紐づけとは別。次のチェックインで端末が名乗れば自己申告として入り直す)
                  </Typography.Text>
                </Section>
              )}
            </>
          ),
        },
        {
          key: "apps",
          label: "アプリ",
          badge: apps.length,
          children: (
            <Section title={`インストール済みアプリ(${apps.length} 件)`} flush>
              <Table<DeviceApp>
                scroll={{ x: "max-content" }}
                rowKey={(row, i) => `${row.name ?? ""}-${i}`}
                size="small"
                columns={appColumns}
                dataSource={apps}
                pagination={false}
                locale={{ emptyText: "アプリ情報なし" }}
              />
            </Section>
          ),
        },
        // 取得元で出すタブを変える。Apple と Android は表が同じでも操作が別物なので、
        // 1 つのタブに混ぜず、その端末に当てはまる方だけ押せる。
        // どちらも**契約にあるときだけ**出す — 台帳は 3 つのうち 1 つでも
        // 買っていれば開くので、無い方のタブを出すと 403 を叩き続ける
        {
          key: "mdm",
          label: "MDM",
          hidden: !hasApple,
          disabled: isAndroid ? "Android 端末に Apple MDM の操作は無い" : false,
          lazy: true,
          children: <MdmTab device={d} open />,
        },
        {
          key: "android",
          label: "Android",
          hidden: !hasAndroid,
          disabled: isAndroid ? false : "この端末は Android ではない",
          lazy: true,
          children: <AndroidTab device={d} open />,
        },
        {
          key: "policies",
          label: "ポリシー",
          badge: policies.length,
          children: (
            <Section title={`ポリシー適用(${policies.length})`} flush>
              <Table<[string, string]>
                scroll={{ x: "max-content" }}
                rowKey={([k]) => k}
                size="small"
                columns={policyColumns}
                dataSource={policies.map(([k, v]) => [k, String(v)] as [string, string])}
                pagination={false}
                locale={{ emptyText: "適用結果なし" }}
              />
            </Section>
          ),
        },
      ]}
    />
  );
}
